import React from 'react'
import {
  CheckSquare,
  Code,
  GripVertical,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Info,
  List,
  ListOrdered,
  Minus,
  Plus,
  Quote,
  Table2,
  Text,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BlogImageUploader } from './BlogImageUploader'

const commandItems = [
  { type: 'paragraph', title: 'Text', description: 'Start writing plain text', icon: Text },
  { type: 'heading1', title: 'Heading 1', description: 'Large section heading', icon: Heading1 },
  { type: 'heading2', title: 'Heading 2', description: 'Medium section heading', icon: Heading2 },
  { type: 'heading3', title: 'Heading 3', description: 'Small section heading', icon: Heading3 },
  { type: 'bullet', title: 'Bullet List', description: 'Create a simple bullet list', icon: List },
  { type: 'numbered', title: 'Numbered List', description: 'Create an ordered list', icon: ListOrdered },
  { type: 'todo', title: 'To-do List', description: 'Track tasks with checkboxes', icon: CheckSquare },
  { type: 'quote', title: 'Quote', description: 'Capture a quote or highlight', icon: Quote },
  { type: 'callout', title: 'Callout', description: 'Add highlighted information', icon: Info },
  { type: 'divider', title: 'Divider', description: 'Separate sections', icon: Minus },
  { type: 'image', title: 'Image', description: 'Upload or insert image', icon: ImageIcon },
  { type: 'mediaText', title: 'Image + Text', description: 'Place text beside an image', icon: ImageIcon },
  { type: 'code', title: 'Code', description: 'Add code snippet', icon: Code },
  { type: 'table', title: 'Table', description: 'Add structured table content', icon: Table2 },
]

const blockLabels = {
  paragraph: 'Text',
  heading1: 'Heading 1',
  heading2: 'Heading 2',
  heading3: 'Heading 3',
  bullet: 'Bullet List',
  numbered: 'Numbered List',
  todo: 'To-do List',
  quote: 'Quote',
  callout: 'Callout',
  divider: 'Divider',
  image: 'Image',
  mediaText: 'Image + Text',
  code: 'Code',
  table: 'Table',
}

function createBlock(type = 'paragraph', content = '') {
  return {
    id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    value: content,
    imageAlt: '',
    caption: '',
    mediaUrl: '',
    mediaAlt: '',
    mediaCaption: '',
    mediaSide: 'left',
    checked: false,
    cells: type === 'table' ? [['', ''], ['', '']] : undefined,
  }
}

function applyMarkdownShortcut(value, currentType) {
  const shortcuts = [
    { pattern: /^#\s$/, type: 'heading1', content: '' },
    { pattern: /^##\s$/, type: 'heading2', content: '' },
    { pattern: /^###\s$/, type: 'heading3', content: '' },
    { pattern: /^-\s$/, type: 'bullet', content: '' },
    { pattern: /^1\.\s$/, type: 'numbered', content: '' },
    { pattern: /^>\s$/, type: 'quote', content: '' },
    { pattern: /^---$/, type: 'divider', content: '' },
  ]
  const match = shortcuts.find((shortcut) => shortcut.pattern.test(value))
  if (!match) return { type: currentType, content: value }
  return { type: match.type, content: match.content }
}

function normalizeBlocks(blocks) {
  if (Array.isArray(blocks) && blocks.length > 0) {
    return blocks.map((block) => ({ ...createBlock(block.type, block.value), ...block }))
  }
  return [createBlock('image'), createBlock('paragraph')]
}

export function NotionBlogEditor({ contentBlocks, onChange, uploading, onUpload }) {
  const [blocks, setBlocks] = React.useState(() => normalizeBlocks(contentBlocks))
  const [activeBlockId, setActiveBlockId] = React.useState(blocks[0]?.id ?? '')
  const [slashMenu, setSlashMenu] = React.useState(null)
  const [slashQuery, setSlashQuery] = React.useState('')
  const [slashIndex, setSlashIndex] = React.useState(0)
  const [draggedIndex, setDraggedIndex] = React.useState(null)
  const blockRefs = React.useRef({})

  const filteredCommands = React.useMemo(() => {
    const query = slashQuery.trim().toLowerCase()
    return commandItems.filter((item) => (
      !query || item.title.toLowerCase().includes(query) || item.description.toLowerCase().includes(query)
    ))
  }, [slashQuery])

  function commitBlocks(nextBlocks) {
    const normalized = nextBlocks.length > 0 ? nextBlocks : [createBlock('paragraph')]
    setBlocks(normalized)
    onChange(normalized)
  }

  function focusBlock(blockId) {
    window.setTimeout(() => blockRefs.current[blockId]?.focus(), 0)
  }

  function updateBlock(blockId, updates) {
    commitBlocks(blocks.map((block) => (block.id === blockId ? { ...block, ...updates } : block)))
  }

  function insertBlockAfter(blockId, type = 'paragraph', showMenu = false) {
    const index = blocks.findIndex((block) => block.id === blockId)
    const nextBlock = createBlock(type)
    const nextBlocks = [...blocks]
    nextBlocks.splice(index + 1, 0, nextBlock)
    commitBlocks(nextBlocks)
    setActiveBlockId(nextBlock.id)
    if (showMenu) {
      setSlashMenu({ blockId: nextBlock.id })
      setSlashQuery('')
      setSlashIndex(0)
    }
    focusBlock(nextBlock.id)
    return nextBlock
  }

  function removeBlock(blockId) {
    if (blocks.length <= 1) {
      updateBlock(blockId, { type: 'paragraph', value: '' })
      return
    }
    const index = blocks.findIndex((block) => block.id === blockId)
    const nextBlocks = blocks.filter((block) => block.id !== blockId)
    const focusTarget = nextBlocks[Math.max(index - 1, 0)]?.id
    commitBlocks(nextBlocks)
    if (focusTarget) focusBlock(focusTarget)
  }

  function openSlashMenu(blockId) {
    setSlashMenu({ blockId })
    setSlashQuery('')
    setSlashIndex(0)
  }

  function convertBlock(blockId, type) {
    const nextBlocks = blocks.map((block) => {
      if (block.id !== blockId) return block
      return { ...createBlock(type, type === 'divider' ? '' : (block.value || '').replace(/^\/[^\s]*/, '')), id: block.id }
    })
    commitBlocks(nextBlocks)
    setSlashMenu(null)
    setSlashQuery('')
    focusBlock(blockId)
  }

  function handleTextChange(block, value) {
    const shortcut = applyMarkdownShortcut(value, block.type)
    updateBlock(block.id, { type: shortcut.type, value: shortcut.content })

    if (value.startsWith('/')) {
      openSlashMenu(block.id)
      setSlashQuery(value.slice(1))
    } else if (slashMenu?.blockId === block.id) {
      setSlashMenu(null)
    }
  }

  function handleKeyDown(event, block, index) {
    if (slashMenu?.blockId === block.id) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setSlashIndex((current) => Math.min(current + 1, filteredCommands.length - 1))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setSlashIndex((current) => Math.max(current - 1, 0))
        return
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        const command = filteredCommands[slashIndex]
        if (command) convertBlock(block.id, command.type)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setSlashMenu(null)
        return
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (['bullet', 'numbered', 'todo'].includes(block.type) && !(block.value || '').trim()) {
        updateBlock(block.id, { type: 'paragraph' })
        return
      }
      insertBlockAfter(block.id)
      return
    }

    if (event.key === 'Backspace' && !block.value && block.type !== 'image' && block.type !== 'table') {
      event.preventDefault()
      removeBlock(block.id)
      return
    }

    if (event.key === '/' && !(block.value || '').trim()) {
      openSlashMenu(block.id)
      return
    }

    if (event.key === 'ArrowUp' && index > 0 && event.currentTarget.selectionStart === 0) {
      focusBlock(blocks[index - 1].id)
    }
    if (event.key === 'ArrowDown' && index < blocks.length - 1 && event.currentTarget.selectionStart === (block.value || '').length) {
      focusBlock(blocks[index + 1].id)
    }
  }

  function handleDrop(targetIndex) {
    if (draggedIndex === null || draggedIndex === targetIndex) return
    const nextBlocks = [...blocks]
    const [movedBlock] = nextBlocks.splice(draggedIndex, 1)
    nextBlocks.splice(targetIndex, 0, movedBlock)
    commitBlocks(nextBlocks)
    setDraggedIndex(null)
  }

  async function uploadEditorImage(file, blockId, field = 'value') {
    const url = await onUpload(file)
    if (url) {
      updateBlock(blockId, field === 'mediaUrl' ? { mediaUrl: url } : { value: url })
    }
  }

  function updateTableCell(block, rowIndex, columnIndex, value) {
    const cells = (block.cells ?? [['', '']]).map((row) => [...row])
    cells[rowIndex][columnIndex] = value
    updateBlock(block.id, { cells })
  }

  function addTableRow(block) {
    const cells = block.cells ?? [['', '']]
    updateBlock(block.id, { cells: [...cells, Array(cells[0]?.length || 2).fill('')] })
  }

  function addTableColumn(block) {
    updateBlock(block.id, { cells: (block.cells ?? [['', '']]).map((row) => [...row, '']) })
  }

  function removeTableRow(block) {
    const cells = block.cells ?? [['', '']]
    updateBlock(block.id, { cells: cells.length > 1 ? cells.slice(0, -1) : cells })
  }

  function removeTableColumn(block) {
    const cells = block.cells ?? [['', '']]
    updateBlock(block.id, { cells: cells[0]?.length > 1 ? cells.map((row) => row.slice(0, -1)) : cells })
  }

  function resizeTextarea(node) {
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }

  function renderTextareaBlock(block, index, extraClassName = '') {
    return (
      <textarea
        ref={(node) => {
          if (node) {
            blockRefs.current[block.id] = node
            resizeTextarea(node)
          }
        }}
        className={`w-full resize-none border-0 bg-transparent text-sm leading-relaxed outline-none placeholder:text-muted-foreground ${
          block.type === 'heading1' ? 'text-2xl font-bold' :
          block.type === 'heading2' ? 'text-xl font-semibold' :
          block.type === 'heading3' ? 'text-lg font-semibold' :
          block.type === 'quote' ? 'border-l-2 border-border pl-3 italic text-muted-foreground' :
          block.type === 'code' ? 'rounded-md bg-muted px-3 py-2 font-mono' : ''
        } ${extraClassName}`}
        rows={block.type === 'code' ? 5 : 1}
        value={block.value || ''}
        placeholder={blocks.length === 1 && !block.value ? "Type '/' for commands" : blockLabels[block.type]}
        onFocus={() => setActiveBlockId(block.id)}
        onChange={(event) => {
          resizeTextarea(event.currentTarget)
          handleTextChange(block, event.target.value)
        }}
        onKeyDown={(event) => handleKeyDown(event, block, index)}
      />
    )
  }

  function renderSlashMenu(blockId, query, setQuery, index, setIndex, onPick) {
    return (
      <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-md border border-border bg-popover shadow-md">
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setIndex(0)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setIndex((current) => Math.min(current + 1, filteredCommands.length - 1))
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault()
              setIndex((current) => Math.max(current - 1, 0))
            }
            if (event.key === 'Enter') {
              event.preventDefault()
              const command = filteredCommands[index]
              if (command) onPick(command.type)
            }
            if (event.key === 'Escape') {
              event.preventDefault()
              setSlashMenu(null)
            }
          }}
          placeholder="Search blocks"
          autoFocus
          className="w-full border-b border-border bg-transparent px-3 py-2 text-sm outline-none"
        />
        <div className="max-h-60 overflow-y-auto p-1">
          {filteredCommands.map((item, itemIndex) => {
            const Icon = item.icon
            return (
              <button
                type="button"
                key={item.type}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${index === itemIndex ? 'bg-accent' : 'hover:bg-accent'}`}
                onMouseEnter={() => setIndex(itemIndex)}
                onClick={() => onPick(item.type)}
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span>
                  <strong className="font-medium">{item.title}</strong>
                  <span className="ml-1 text-xs text-muted-foreground">{item.description}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1 rounded-md border border-border p-3">
      {blocks.map((block, index) => (
        <div
          key={block.id}
          className={`group relative flex items-start gap-1 rounded-md px-1 py-1 ${activeBlockId === block.id ? 'bg-accent/40' : ''}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => handleDrop(index)}
        >
          <div className="mt-1 flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100">
            <button type="button" aria-label="Add block below" className="rounded p-0.5 hover:bg-accent" onClick={() => insertBlockAfter(block.id, 'paragraph', true)}>
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={`Drag ${blockLabels[block.type]} block`}
              className="cursor-grab rounded p-0.5 hover:bg-accent"
              draggable
              onDragStart={() => setDraggedIndex(index)}
              onDragEnd={() => setDraggedIndex(null)}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
            <button type="button" aria-label={`Remove ${blockLabels[block.type]} block`} className="rounded p-0.5 text-destructive hover:bg-accent" onClick={() => removeBlock(block.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            {block.type === 'divider' ? (
              <button type="button" aria-label="Divider block" className="my-2 h-px w-full bg-border" onClick={() => setActiveBlockId(block.id)} />
            ) : block.type === 'image' ? (
              <BlogImageUploader
                image={block.value}
                imageAlt={block.imageAlt}
                uploading={uploading}
                onUpload={(file) => uploadEditorImage(file, block.id)}
                onImageChange={(value) => updateBlock(block.id, { value })}
              />
            ) : block.type === 'mediaText' ? (
              <div className={`grid gap-4 sm:grid-cols-2 ${block.mediaSide === 'right' ? 'sm:[&>*:first-child]:order-2' : ''}`}>
                <BlogImageUploader
                  image={block.mediaUrl}
                  imageAlt={block.mediaAlt}
                  uploading={uploading}
                  onUpload={(file) => uploadEditorImage(file, block.id, 'mediaUrl')}
                  onImageChange={(value) => updateBlock(block.id, { mediaUrl: value })}
                />
                <div>{renderTextareaBlock(block, index)}</div>
              </div>
            ) : block.type === 'table' ? (
              <div className="space-y-2">
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    {(block.cells ?? [['', '']]).map((row, rowIndex) => (
                      <tr key={`${block.id}-row-${rowIndex}`}>
                        {row.map((cell, columnIndex) => (
                          <td key={`${block.id}-cell-${rowIndex}-${columnIndex}`} className="border border-border p-1">
                            <input
                              value={cell}
                              onChange={(event) => updateTableCell(block, rowIndex, columnIndex, event.target.value)}
                              placeholder="Cell"
                              className="w-full bg-transparent px-1 py-0.5 text-sm outline-none"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex gap-1.5">
                  <Button type="button" variant="outline" size="sm" onClick={() => addTableRow(block)}>+ Row</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => addTableColumn(block)}>+ Column</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => removeTableRow(block)}>- Row</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => removeTableColumn(block)}>- Column</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                {block.type === 'bullet' ? <span className="mt-1.5 text-muted-foreground">•</span> : null}
                {block.type === 'numbered' ? <span className="mt-1 text-sm text-muted-foreground">{index + 1}.</span> : null}
                {block.type === 'todo' ? (
                  <input
                    type="checkbox"
                    className="mt-1.5"
                    checked={Boolean(block.checked)}
                    onChange={(event) => updateBlock(block.id, { checked: event.target.checked })}
                  />
                ) : null}
                {block.type === 'callout' ? <Info className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" /> : null}
                <div className={`flex-1 ${block.type === 'callout' ? 'rounded-md bg-muted px-3 py-2' : ''}`}>
                  {renderTextareaBlock(block, index)}
                </div>
              </div>
            )}
          </div>

          {slashMenu?.blockId === block.id
            ? renderSlashMenu(block.id, slashQuery, setSlashQuery, slashIndex, setSlashIndex, (type) => convertBlock(block.id, type))
            : null}
        </div>
      ))}

      <Button type="button" variant="ghost" size="sm" className="mt-1" onClick={() => insertBlockAfter(blocks[blocks.length - 1].id, 'paragraph', true)}>
        <Plus className="mr-1.5 h-4 w-4" /> Add block
      </Button>
    </div>
  )
}
